import { Camera } from "lucide-react";
import { useEffect, useState } from "react";
import { Icon } from "../icons/Icon";
import { useUiText } from "../primitives/uiText";
import { cx } from "../styles";

/**
 * A photo from the phone's camera (EP10-S17): the native file input with
 * `capture`, which opens the rear camera on a phone and a file picker on a
 * desktop, with no permission prompt and no camera library. The photo is
 * scaled down to {@link MAX_EDGE} and re-encoded as JPEG before it is handed
 * over, so what waits on the phone for a signal is a few hundred kilobytes,
 * not a 12-megapixel original.
 */

/** The longest edge of a captured photo, in pixels. */
export const MAX_EDGE = 1600;

/**
 * The photo scaled so its longest edge is at most `maxEdge`, as a JPEG. Where
 * the browser cannot decode or draw it (an old WebView, a test), the file as
 * taken: a large photo is better than none.
 */
export async function compressPhoto(file: Blob, maxEdge = MAX_EDGE): Promise<Blob> {
	try {
		const bitmap = await createImageBitmap(file);
		const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
		const canvas = document.createElement("canvas");
		canvas.width = Math.round(bitmap.width * scale);
		canvas.height = Math.round(bitmap.height * scale);
		const context = canvas.getContext("2d");
		if (context === null) {
			return file;
		}
		context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		bitmap.close();
		const jpeg = await new Promise<Blob | null>((resolve) =>
			canvas.toBlob(resolve, "image/jpeg", 0.8),
		);
		return jpeg ?? file;
	} catch {
		return file;
	}
}

export interface CameraCaptureProps {
	/** The button's words before a photo is taken. */
	label: string;
	/** The button's words once there is one. */
	retakeLabel: string;
	/** Names the preview for a screen reader. */
	previewLabel: string;
	photo: Blob | null;
	/**
	 * Takes the prepared photo. May return a promise — a save to the phone's
	 * queue — and a rejection is shown under the button (ADJ-M03-1): a full
	 * phone used to lose the photo without a word.
	 */
	onCapture: (photo: Blob) => unknown;
}

export function CameraCapture({
	label,
	retakeLabel,
	previewLabel,
	photo,
	onCapture,
}: CameraCaptureProps) {
	const t = useUiText();
	const [url, setUrl] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [failed, setFailed] = useState(false);
	useEffect(() => {
		if (photo === null || typeof URL.createObjectURL !== "function") {
			setUrl(null);
			return;
		}
		const next = URL.createObjectURL(photo);
		setUrl(next);
		return () => URL.revokeObjectURL(next);
	}, [photo]);

	async function take(file: File) {
		setBusy(true);
		setFailed(false);
		try {
			await onCapture(await compressPhoto(file));
		} catch {
			setFailed(true);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="flex flex-col gap-2">
			{url !== null && (
				<img src={url} alt={previewLabel} className="max-h-48 self-start rounded-md" />
			)}
			<label
				aria-busy={busy || undefined}
				className={cx(
					"flex min-h-12 items-center justify-center gap-2 rounded-lg border border-border bg-bg-surface px-4",
					"text-[14px] font-bold text-fg focus-within:ring-3 focus-within:ring-ring-halo",
					busy ? "cursor-wait text-fg-subtle" : "cursor-pointer",
				)}
			>
				{busy ? (
					<span
						aria-hidden="true"
						className="inline-block size-[18px] shrink-0 animate-[km-spin_0.8s_linear_infinite] rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
					/>
				) : (
					<Icon as={Camera} size={18} />
				)}
				{busy ? t("ui.camera.preparing") : photo === null ? label : retakeLabel}
				<input
					type="file"
					accept="image/*"
					capture="environment"
					className="sr-only"
					disabled={busy}
					onChange={(e) => {
						const file = e.target.files?.[0];
						// Cleared, so taking the same photo again still fires.
						e.target.value = "";
						if (file !== undefined) {
							void take(file);
						}
					}}
				/>
			</label>
			{failed && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{t("ui.camera.failed")}
				</p>
			)}
		</div>
	);
}
